import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { toast } from "react-toastify";
import { useContext, useEffect, useRef, useState } from "react";
import RangeSlider from "../../components/forms/RangeSlider/RangeSlider";
import SingleMachineCameraFeed from "../../components/FeatureControls/custom-control-components/claw-camera-feeds/SingleMachineCameraFeed";
import { AuthContext } from "../../contexts/AuthContext";
import { ACCOUNT_TYPES } from "../../helpers/AccountTypes";
import "./ClawDebugTools.scss";

const STATUS_IDLE_REFRESH_INTERVAL_MS = 15000;
const STATUS_ACTIVE_REFRESH_INTERVAL_MS = 500;

const STATUS_LABELS = {
    WAITING_FOR_MOVE: 'Waiting for Move',
    IN_PLAY: 'In Play',
    AWAITING_RESULT: 'Awaiting Result',
    IDLE: 'Idle',
    MOVE_STARTING: 'Move Starting',
    MOVING_PLAYING_FIELD: 'Moving Playing Field',
    GRABBING: 'Grabbing',
    MOVING_TO_DROP_ZONE: 'Moving to Drop Zone',
    MOVE_FINISHED: 'Move Finished',
    ERROR: 'Error',
};

function formatStatusTime(timestamp) {
    if (!timestamp) return 'No recent activity';

    const parsedDate = new Date(timestamp);
    if (Number.isNaN(parsedDate.getTime())) return 'No recent activity';

    return parsedDate.toLocaleString();
}

function formatStatusLabel(statusKey, fallback = 'Unknown') {
    return STATUS_LABELS[statusKey] || fallback;
}

function formatCountdown(milliseconds) {
    if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '0s';

    const totalSeconds = Math.ceil(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes <= 0) return `${seconds}s`;
    return `${minutes}m ${seconds}s`;
}

function formatInlineSummary(summary) {
    if (!summary) return 'No machine messages recorded yet';

    const parts = [];

    if (summary.status) parts.push(`status ${summary.status}`);
    if (summary.info) parts.push(`info ${summary.info}`);
    if (summary.type) parts.push(`type ${summary.type}`);
    if (summary.id) parts.push(`id ${summary.id}`);

    return parts.length > 0 ? parts.join(' • ') : 'Message received';
}

function getRefreshCountdown(lastUpdatedAt, refreshIntervalMs, currentTimeMs) {
    if (!lastUpdatedAt) return formatCountdown(refreshIntervalMs);

    const lastUpdatedTime = new Date(lastUpdatedAt).getTime();
    if (Number.isNaN(lastUpdatedTime)) return formatCountdown(refreshIntervalMs);

    const remainingMs = Math.max(refreshIntervalMs - (currentTimeMs - lastUpdatedTime), 0);
    return formatCountdown(remainingMs);
}

function renderInfoValue(value, fallback = 'n/a') {
    if (value === null || value === undefined || value === '') return fallback;
    return value;
}

function getSessionTone(machineStatus) {
    if (machineStatus?.sessionStarted) return 'online';
    if (machineStatus?.monitoringMode === 'restarting') return 'warning';
    return 'unknown';
}

function getMonitoringTone(machineStatus) {
    if (machineStatus?.monitoringMode === 'restarting' || machineStatus?.monitoringMode === 'paused') return 'warning';
    if (machineStatus?.monitoringMode === 'polling' || machineStatus?.monitoringMode === 'polling-enabled' || machineStatus?.monitoringMode === 'move-active') return 'online';
    if (machineStatus?.connectionHealth === 'disconnected') return 'offline';
    return 'unknown';
}

function getSocketActivityTone(machineStatus) {
    if (machineStatus?.socketState === 'connected' || machineStatus?.socketConnected) return 'online';
    if (machineStatus?.socketState === 'error') return 'offline';
    if (machineStatus?.monitoringMode === 'restarting') return 'warning';
    return 'unknown';
}

function Home() {
    const navigate = useNavigate();
    const [authContext] = useContext(AuthContext);
    const machineLabels = {
        1: 'Left Machine (New York)',
        2: 'Right Machine (New York)',
    };
    
    // Left machine controls
    const [xPercLeft, setXPercLeft] = useState(50);
    const [yPercLeft, setYPercLeft] = useState(50);
    
    // Right machine controls
    const [xPercRight, setXPercRight] = useState(50);
    const [yPercRight, setYPercRight] = useState(50);
    const [machineStatuses, setMachineStatuses] = useState({});
    const [statusMeta, setStatusMeta] = useState({});
    const [statusRequestState, setStatusRequestState] = useState({ loading: true, error: null, lastUpdatedAt: null });
    const [pollingRequestInFlight, setPollingRequestInFlight] = useState(false);
    const [statusUiTick, setStatusUiTick] = useState(Date.now());
    const [expandedStatusPanels, setExpandedStatusPanels] = useState({ 1: false, 2: false });
    const [forceRefreshInFlight, setForceRefreshInFlight] = useState(false);
    const statusRequestInFlightRef = useRef(false);

const canControlPolling = (authContext?.userData?.userLevel || 0) >= ACCOUNT_TYPES.koko.level
    && Boolean(statusMeta?.pollingControlAvailable);

const hasActiveMove = Object.values(machineStatuses).some((machineStatus) => (
    machineStatus?.overallStatus === 'IN_PLAY'
    || machineStatus?.overallStatus === 'AWAITING_RESULT'
    || machineStatus?.sessionStarted
));

const statusRefreshInterval = hasActiveMove
    ? STATUS_ACTIVE_REFRESH_INTERVAL_MS
    : STATUS_IDLE_REFRESH_INTERVAL_MS;

const refreshMachineStatuses = async (isSilent = false, forceProbe = false) => {
    if (statusRequestInFlightRef.current) {
        return;
    }

    statusRequestInFlightRef.current = true;

    if (forceProbe) {
        setForceRefreshInFlight(true);
    }

    if (!isSilent) {
        setStatusRequestState((currentState) => ({ ...currentState, loading: true, error: null }));
    }

    try {
        const response = await axios.get(forceProbe ? 'claw/status/refresh' : 'claw/status', { withCredentials: true });
        const nextStatuses = (response.data?.machines || []).reduce((statusMap, machineStatus) => {
            statusMap[machineStatus.machineID] = machineStatus;
            return statusMap;
        }, {});

        setMachineStatuses(nextStatuses);
        setStatusMeta(response.data?.meta || {});
        setStatusRequestState({ loading: false, error: null, lastUpdatedAt: new Date().toISOString() });
    } catch (error) {
        setStatusRequestState({
            loading: false,
            error: error.response?.data?.message || error.message,
            lastUpdatedAt: new Date().toISOString(),
        });
    } finally {
        statusRequestInFlightRef.current = false;
        if (forceProbe) {
            setForceRefreshInFlight(false);
        }
    }
}

useEffect(() => {
    refreshMachineStatuses();

    return undefined;
}, []);

useEffect(() => {
    const intervalId = window.setInterval(() => {
        refreshMachineStatuses(true);
    }, statusRefreshInterval);

    return () => window.clearInterval(intervalId);
}, [statusRefreshInterval]);

useEffect(() => {
    const intervalId = window.setInterval(() => {
        setStatusUiTick(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
}, []);

const getMachineStatusTone = (machineStatus) => {
    if (!machineStatus) return 'unknown';
    if (machineStatus.monitoringMode === 'restarting') return 'warning';
    if (machineStatus.connectionHealth === 'disconnected' || machineStatus.socketState === 'error') return 'offline';
    if (machineStatus.monitoringMode === 'paused') return 'warning';
    if (machineStatus.socketConnected || machineStatus.sessionStarted || machineStatus.connectionHealth === 'connected' || machineStatus.connectionHealth === 'last-known') return 'online';
    return 'unknown';
}

const getMonitoringModeLabel = (machineStatus) => {
    switch (machineStatus?.monitoringMode) {
        case 'polling':
            return 'TCP Polling Active';
        case 'polling-enabled':
            return 'TCP Polling Armed';
        case 'restarting':
            return 'Restart Initiated';
        case 'paused':
            return 'TCP Polling Paused';
        case 'move-active':
            return 'Move Active';
        case 'passive-live-socket':
            return 'Manual Live Socket';
        case 'passive':
        default:
            return 'Manual Monitoring';
    }
}

const getConnectionLabel = (machineStatus) => {
    if (machineStatus?.monitoringMode === 'restarting') {
        return 'Restart Initiated';
    }

    switch (machineStatus?.connectionHealth) {
        case 'connected':
            return 'Connected';
        case 'last-known':
            return 'Last Known Healthy';
        case 'disconnected':
            return 'Disconnected';
        default:
            return 'Unknown';
    }
}

const getPrimaryStatusLabel = (machineStatus) => {
    if (!machineStatus) return 'Status Unavailable';
    if (machineStatus.monitoringMode === 'restarting') return 'Restart Initiated';
    if (machineStatus.connectionHealth === 'disconnected') return 'Disconnected';
    if (machineStatus.movePhase && machineStatus.movePhase !== 'IDLE') {
        return formatStatusLabel(machineStatus.movePhase, machineStatus.movePhase);
    }
    return formatStatusLabel(machineStatus.overallStatus, machineStatus.overallStatus || 'Unknown');
}

const shouldShowDisconnectedOverlay = (machineStatus) => {
    return machineStatus?.connectionHealth === 'disconnected'
        && machineStatus?.monitoringMode !== 'restarting'
        && !machineStatus?.sessionStarted;
}

const shouldShowRestartOverlay = (machineStatus) => {
    return machineStatus?.monitoringMode === 'restarting';
}

const togglePolling = async (shouldStart) => {
    setPollingRequestInFlight(true);

    try {
        await axios.post(`claw/polling/${shouldStart ? 'start' : 'stop'}`, {}, { withCredentials: true });
        toast.success(`TCP polling ${shouldStart ? 'started' : 'stopped'}`);
        refreshMachineStatuses(true);
    } catch (error) {
        toast.error('Error - ' + (error.response?.data?.message || error.message));
    } finally {
        setPollingRequestInFlight(false);
    }
}

const renderDisconnectedOverlay = () => {
    return (
        <div className="claw-camera-warning-overlay">
            <div className="claw-camera-warning-card">
                <span className="claw-camera-warning-icon">!</span>
                <strong>Disconnected</strong>
                <span>Please reboot!</span>
            </div>
        </div>
    );
}

const renderRestartOverlay = () => {
    return (
        <div className="claw-camera-warning-overlay claw-camera-warning-overlay--restart">
            <div className="claw-camera-warning-card claw-camera-warning-card--restart">
                <span className="claw-camera-warning-icon claw-camera-warning-icon--restart">!</span>
                <strong>Restart Initiated</strong>
                <span>Waiting for the claw service to come back</span>
            </div>
        </div>
    );
}

const renderCameraStatus = (machineID) => {
    const machineStatus = machineStatuses[machineID];

    return (
        <div className="claw-camera-status-strip">
            <p className="claw-camera-status-kicker">Live Move Status</p>
            <h3>{getPrimaryStatusLabel(machineStatus)}</h3>
            <div className="claw-camera-status-meta">
                <span>{getMonitoringModeLabel(machineStatus)}</span>
                <span>{getConnectionLabel(machineStatus)}</span>
                <span>Last detected {formatStatusTime(machineStatus?.lastDetectedStatusAt || machineStatus?.lastMessageAt || machineStatus?.movePhaseUpdatedAt)}</span>
            </div>
        </div>
    );
}

const renderMachineStatus = (machineID) => {
    const machineStatus = machineStatuses[machineID];
    const statusTone = getMachineStatusTone(machineStatus);
    const connectionLabel = getConnectionLabel(machineStatus);
    const topLevelStatusLabel = formatStatusLabel(machineStatus?.overallStatus, 'Unknown');
    const probeModeLabel = getMonitoringModeLabel(machineStatus);
    const displayError = machineStatus?.lastError || 'None recorded';
    const sessionTone = getSessionTone(machineStatus);
    const monitoringTone = getMonitoringTone(machineStatus);
    const socketTone = getSocketActivityTone(machineStatus);
    const isExpanded = Boolean(expandedStatusPanels[machineID]);
    const refreshCountdown = getRefreshCountdown(statusRequestState.lastUpdatedAt, statusRefreshInterval, statusUiTick);
    const snapshotButtonLabel = forceRefreshInFlight ? 'Running TCP Probe...' : statusRequestState.loading ? 'Refreshing Snapshot...' : 'Refresh Snapshot';
    const statusSummary = machineStatus?.monitoringMode === 'restarting'
        ? 'Soft recovery has been initiated. Polling is temporarily paused while the claw service restarts.'
        : machineStatus?.connectionHealth === 'disconnected'
        ? 'Machine is disconnected. Reboot is likely required.'
        : machineStatus?.probe?.reason === 'post-move-pause'
            ? 'Monitoring is paused after the previous move before TCP checks resume.'
            : machineStatus?.probe?.reason === 'move-active'
                ? 'Monitoring is paused while the move is active.'
                : machineStatus?.probe?.reason === 'probe-failed'
                    ? 'The last TCP check failed. Review the last error below.'
                    : 'Status is being tracked manually until a direct TCP check runs.';

    return (
        <div className="claw-status-panel">
            <div className="claw-status-heading-row">
                <div>
                    <p className="claw-status-kicker">Connection Monitor</p>
                    <h3>Machine Status Monitor</h3>
                </div>
                <span className={`claw-status-badge ${statusTone}`}>{connectionLabel}</span>
            </div>

            <p className="claw-status-summary">{statusSummary}</p>

            <div className="claw-status-refresh-row claw-status-refresh-row--top">
                <p className="claw-status-refresh-note">
                    Refresh Snapshot runs a one-off TCP probe when the machine is eligible, then updates the dashboard with the fresh backend status.
                </p>
                <div className="claw-status-refresh-footer">
                    <p className="claw-status-refresh-meta">
                        <span className="claw-status-refresh-meta-label">Last snapshot</span>
                        <strong>{formatStatusTime(statusRequestState.lastUpdatedAt)}</strong>
                        <small>{statusRequestState.loading ? 'Refreshing now' : `Next auto refresh in ${refreshCountdown}`}</small>
                    </p>
                    <button
                        className="standard-button claw-status-refresh"
                        disabled={statusRequestState.loading || forceRefreshInFlight}
                        onClick={() => refreshMachineStatuses(false, true)}
                    >
                        {snapshotButtonLabel}
                    </button>
                </div>
            </div>

            <button
                className="claw-status-toggle"
                type="button"
                onClick={() => setExpandedStatusPanels((currentState) => ({
                    ...currentState,
                    [machineID]: !currentState[machineID],
                }))}
            >
                <span>{isExpanded ? 'Hide Detailed Status' : 'Show Detailed Status'}</span>
                <span className={`claw-status-toggle-icon ${isExpanded ? 'is-expanded' : ''}`}>⌄</span>
            </button>

            {isExpanded && (
                <>
                    <div className="claw-status-grid">
                        <div className={`claw-status-stat-card claw-status-stat-card--${statusTone}`}>
                            <span className="claw-status-label">Overall Status</span>
                            <strong>{topLevelStatusLabel}</strong>
                        </div>
                        <div className={`claw-status-stat-card claw-status-stat-card--${statusTone}`}>
                            <span className="claw-status-label">Connection</span>
                            <strong>{connectionLabel}</strong>
                            <small className={`claw-inline-note claw-inline-note--${statusTone}`}>{machineStatus?.socketConnected ? 'Live socket open' : 'Socket idle'}</small>
                        </div>
                        <div className="claw-status-stat-card claw-status-stat-card--unknown">
                            <span className="claw-status-label">Latency</span>
                            <strong>{machineStatus?.probe?.lastLatencyMs ? `${machineStatus.probe.lastLatencyMs}ms` : 'n/a'}</strong>
                        </div>
                        <div className={`claw-status-stat-card claw-status-stat-card--${sessionTone}`}>
                            <span className="claw-status-label">Session</span>
                            <strong>{machineStatus?.sessionStarted ? 'Move Active' : 'No Session'}</strong>
                        </div>
                        <div className={`claw-status-stat-card claw-status-stat-card--wide claw-status-stat-card--${monitoringTone}`}>
                            <span className="claw-status-label">Monitoring Mode</span>
                            <strong>{probeModeLabel}</strong>
                            <small className={`claw-inline-note claw-inline-note--${monitoringTone}`}>{machineStatus?.probe?.reason === 'move-active' ? 'Paused during live move' : 'Monitoring state from backend'}</small>
                        </div>
                    </div>

                    <div className="claw-status-details">
                        <p className="claw-status-detail-card claw-status-detail-card--full">
                            <span className="claw-status-label">Last Message Summary</span>
                            <strong>{formatInlineSummary(machineStatus?.lastMessageSummary)}</strong>
                            <small>{formatStatusTime(machineStatus?.lastMessageAt)}</small>
                        </p>
                        <p className={`claw-status-detail-card claw-status-detail-card--${socketTone}`}>
                            <span className="claw-status-label">Socket Activity</span>
                            <strong>{renderInfoValue(machineStatus?.socketState, 'unknown')}</strong>
                            <small>{formatStatusTime(machineStatus?.lastSocketEventAt)}</small>
                        </p>
                        <p className="claw-status-detail-card">
                            <span className="claw-status-label">Last Error</span>
                            <strong>{displayError}</strong>
                            <small>{machineStatus?.connectionHealth === 'disconnected' ? 'Needs operator attention' : 'No active fault'}</small>
                        </p>
                        <p className="claw-status-detail-card">
                            <span className="claw-status-label">Last TCP Check</span>
                            <strong>{formatStatusTime(machineStatus?.probe?.checkedAt)}</strong>
                            <small>{machineStatus?.probe?.lastLatencyMs ? `${machineStatus.probe.lastLatencyMs}ms latency` : 'No recent latency sample'}</small>
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}

const clawAutomatePlayTest = (machineID, xPerc, yPerc) => {
    axios
    .post('claw/automate_play_action', {xPerc:xPerc, yPerc:yPerc, machineID:parseInt(machineID), withCredentials:true})
    .then(function (response) {
        console.log(response);
        toast.success(`Machine ${machineID === 1 ? 'LEFT' : 'RIGHT'} moved successfully!`);
        refreshMachineStatuses(true);
    })
    .catch(function (error) {
        toast.error('Error - ' + (error.response?.data?.message || error.message));
    })
}
const clawResetMachine = (machineID) => {
    axios
    .post('claw/reset_claw_session', {machineID:parseInt(machineID), withCredentials:true})
    .then(function (response) {
        console.log(response);
        toast.success('Machine '+ machineID+' Session Reset - Success');
        refreshMachineStatuses(true);
    })
    .catch(function (error) {
        toast.error('Error - ' + (error.response?.data?.message || error.message));
    })
}
const clawRebootMachine = (machineID) => {
    axios
    .post('claw/reboot_machine', {machineID:parseInt(machineID), withCredentials:true})
    .then(function (response) {
        console.log(response);
        toast.success('Machine '+ machineID+' Rebooting - Please Wait');
        refreshMachineStatuses(true);
    })
    .catch(function (error) {
        toast.error('Error - ' + (error.response?.data?.message || error.message));
    })
}

  return (
      <div className="claw-debug-container">
          <div className="claw-debug-shell">
              <div className="claw-debug-header">
                  <p className="claw-debug-eyebrow">Live Machine Control</p>
                  <h1>Claw Machine Debug Tools</h1>
                  <p className="claw-debug-copy">
                      Monitor both cabinets, adjust movement ranges, and send direct recovery commands from a cleaner operator view.
                  </p>
                  {canControlPolling && (
                      <div className="claw-polling-toolbar">
                          <div>
                              <p className="claw-status-kicker">Direct TCP Polling</p>
                              <strong>{statusMeta?.pollingEnabled ? 'Running on this direct controller' : 'Stopped on this direct controller'}</strong>
                          </div>
                          <div className="claw-polling-toolbar-actions">
                              <button
                                  className="standard-button"
                                  type="button"
                                  disabled={pollingRequestInFlight || statusMeta?.pollingEnabled}
                                  onClick={() => togglePolling(true)}
                              >
                                  Start Polling
                              </button>
                              <button
                                  className="warning-button"
                                  type="button"
                                  disabled={pollingRequestInFlight || !statusMeta?.pollingEnabled}
                                  onClick={() => togglePolling(false)}
                              >
                                  Stop Polling
                              </button>
                          </div>
                      </div>
                  )}
                  {statusRequestState.error && <p className="claw-status-error">Status polling error: {statusRequestState.error}</p>}
              </div>
          
              <div className="claw-machines-container">
              
              <div className="claw-machine-column left-machine">
                  <div className="claw-machine-heading">
                      <div>
                          <p className="claw-machine-kicker">Cabinet 01</p>
                          <h2>{machineLabels[1]}</h2>
                      </div>
                  </div>
                  <div className="claw-machine-body">
                      <div className="claw-camera-pane">
                          <SingleMachineCameraFeed
                              machineID={1}
                              machineName={machineLabels[1]}
                              overlayContent={shouldShowRestartOverlay(machineStatuses[1]) ? renderRestartOverlay() : shouldShowDisconnectedOverlay(machineStatuses[1]) ? renderDisconnectedOverlay() : null}
                              footerContent={renderCameraStatus(1)}
                          />
                      </div>
                      <div className="claw-controls-section">
                          <div className="claw-local-controls claw-local-controls-full-width">
                              {renderMachineStatus(1)}
                          </div>
                          <div className="claw-control-workbench">
                              <div className="claw-movement-panel">
                                  <p className="claw-status-kicker">Test Controls</p>
                                  <h3>Move Targets</h3>
                                  <RangeSlider
                                      label={'X Percentage'}
                                      labelNote={''}
                                      setFunc={(newVal) => { setXPercLeft(newVal) }}
                                      min={0}
                                      max={100}
                                      value={xPercLeft}
                                      showCurrentValue={true}
                                  />
                                  
                                  <RangeSlider
                                      label={'Y Percentage'}
                                      labelNote={''}
                                      setFunc={(newVal) => { setYPercLeft(newVal) }}
                                      min={0}
                                      max={100}
                                      value={yPercLeft}
                                      showCurrentValue={true}
                                  />
                              </div>
                              <div className="claw-action-panel">
                                  <p className="claw-status-kicker">Recovery Actions</p>
                                  <h3>Machine Actions</h3>
                                  <div className="claw-button-group">
                                      <button 
                                          className="standard-button" 
                                          onClick={() => clawAutomatePlayTest(1, xPercLeft, yPercLeft)}
                                      >
                                          Play Left Claw Move
                                      </button>
                                      
                                      <button 
                                          className="standard-button" 
                                          onClick={() => clawResetMachine(1)}
                                      >
                                          Reset Left Session
                                      </button>
                                      
                                      <button 
                                          className="warning-button" 
                                          onClick={() => clawRebootMachine(1)}
                                      >
                                          Force Reboot Left Machine
                                      </button>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>

              <div className="claw-machine-column right-machine">
                  <div className="claw-machine-heading">
                      <div>
                          <p className="claw-machine-kicker">Cabinet 02</p>
                          <h2>{machineLabels[2]}</h2>
                      </div>
                  </div>
                  <div className="claw-machine-body">
                      <div className="claw-camera-pane">
                          <SingleMachineCameraFeed
                              machineID={2}
                              machineName={machineLabels[2]}
                              overlayContent={shouldShowRestartOverlay(machineStatuses[2]) ? renderRestartOverlay() : shouldShowDisconnectedOverlay(machineStatuses[2]) ? renderDisconnectedOverlay() : null}
                              footerContent={renderCameraStatus(2)}
                          />
                      </div>
                      <div className="claw-controls-section">
                          <div className="claw-local-controls claw-local-controls-full-width">
                              {renderMachineStatus(2)}
                          </div>
                          <div className="claw-control-workbench">
                              <div className="claw-movement-panel">
                                  <p className="claw-status-kicker">Test Controls</p>
                                  <h3>Move Targets</h3>
                                  <RangeSlider
                                      label={'X Percentage'}
                                      labelNote={''}
                                      setFunc={(newVal) => { setXPercRight(newVal) }}
                                      min={0}
                                      max={100}
                                      value={xPercRight}
                                      showCurrentValue={true}
                                  />
                                  
                                  <RangeSlider
                                      label={'Y Percentage'}
                                      labelNote={''}
                                      setFunc={(newVal) => { setYPercRight(newVal) }}
                                      min={0}
                                      max={100}
                                      value={yPercRight}
                                      showCurrentValue={true}
                                  />
                              </div>
                              <div className="claw-action-panel">
                                  <p className="claw-status-kicker">Recovery Actions</p>
                                  <h3>Machine Actions</h3>
                                  <div className="claw-button-group">
                                      <button 
                                          className="standard-button" 
                                          onClick={() => clawAutomatePlayTest(2, xPercRight, yPercRight)}
                                      >
                                          Play Right Claw Move
                                      </button>
                                      
                                      <button 
                                          className="standard-button" 
                                          onClick={() => clawResetMachine(2)}
                                      >
                                          Reset Right Session
                                      </button>
                                      
                                      <button 
                                          className="warning-button" 
                                          onClick={() => clawRebootMachine(2)}
                                      >
                                          Force Reboot Right Machine
                                      </button>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
          </div>
      </div>
  );
}

export default Home;
